import React, { useState, useCallback } from "react";
import OutlineItemComponent from "./OutlineItemComponent";
import { OutlineItem, OutlinerProps } from "./types";
import styles from "./outliner.module.css";

// Utility: Generate unique ID
const generateId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Utility: Find item in tree
const findItem = (items: OutlineItem[], id: string): OutlineItem | null => {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children?.length) {
      const found = findItem(item.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Utility: Find parent of item
const findParent = (
  items: OutlineItem[],
  childId: string,
  parent: OutlineItem | null = null
): OutlineItem | null => {
  for (const item of items) {
    if (item.id === childId) return parent;
    const found = findParent(item.children, childId, item);
    if (found !== null) return found;
  }
  return null;
};

// Utility: Update item in tree
const updateItemInTree = (
  items: OutlineItem[],
  id: string,
  updates: Partial<OutlineItem>
): OutlineItem[] => {
  return items.map((item) => {
    if (item.id === id) {
      return { ...item, ...updates };
    }
    if (item.children?.length) {
      return { ...item, children: updateItemInTree(item.children, id, updates) };
    }
    return item;
  });
};

// Utility: Delete item from tree
const deleteItemFromTree = (items: OutlineItem[], id: string): OutlineItem[] => {
  return items
    .filter((item) => item.id !== id)
    .map((item) => {
      if (item.children?.length) {
        return { ...item, children: deleteItemFromTree(item.children, id) };
      }
      return item;
    });
};

// Utility: Add sibling after item
const addSiblingAfter = (
  items: OutlineItem[],
  id: string,
  parentId: string | undefined,
  newItem: OutlineItem
): OutlineItem[] => {
  if (!parentId) {
    // Root level
    const index = items.findIndex((item) => item.id === id);
    if (index !== -1) {
      const newItems = [...items];
      newItems.splice(index + 1, 0, newItem);
      return newItems;
    }
    return items;
  }

  return items.map((item) => {
    if (item.id === parentId) {
      const index = item.children.findIndex((child) => child.id === id);
      if (index !== -1) {
        const newChildren = [...item.children];
        newChildren.splice(index + 1, 0, newItem);
        return { ...item, children: newChildren };
      }
    }
    if (item.children?.length) {
      return { ...item, children: addSiblingAfter(item.children, id, parentId, newItem) };
    }
    return item;
  });
};

// Utility: Indent item (make it child of previous sibling)
const indentItem = (
  items: OutlineItem[],
  id: string,
  parentId: string | undefined
): OutlineItem[] => {
  const parent = parentId ? findItem(items, parentId) : null;
  const siblings = parent ? parent.children : items;
  const index = siblings.findIndex((s) => s.id === id);

  if (index <= 0) return items; // Can't indent first item

  const item = siblings[index];
  const previousSibling = siblings[index - 1];

  // Remove item from current location
  let newTree = deleteItemFromTree(items, id);

  // Add as child of previous sibling
  newTree = updateItemInTree(newTree, previousSibling.id, {
    children: [...previousSibling.children, item],
    expanded: true, // Auto-expand when adding children
  });

  return newTree;
};

// Utility: Outdent item (promote to parent's level)
const outdentItem = (
  items: OutlineItem[],
  id: string,
  parentId: string | undefined
): OutlineItem[] => {
  if (!parentId) return items; // Already at root

  const parent = findItem(items, parentId);
  if (!parent) return items;

  const item = findItem(items, id);
  if (!item) return items;

  // Remove from parent's children
  let newTree = updateItemInTree(items, parentId, {
    children: parent.children.filter((child) => child.id !== id),
  });

  // Find grandparent
  const grandparent = findParent(newTree, parentId);
  const grandparentChildren = grandparent ? grandparent.children : newTree;
  const parentIndex = grandparentChildren.findIndex((i) => i.id === parentId);

  if (grandparent) {
    // Insert after parent in grandparent's children
    newTree = updateItemInTree(newTree, grandparent.id, {
      children: [
        ...grandparentChildren.slice(0, parentIndex + 1),
        item,
        ...grandparentChildren.slice(parentIndex + 1),
      ],
    });
  } else {
    // Parent is at root, insert after parent at root
    newTree = [
      ...newTree.slice(0, parentIndex + 1),
      item,
      ...newTree.slice(parentIndex + 1),
    ];
  }

  return newTree;
};

const Outliner: React.FC<OutlinerProps> = ({
  data,
  onChange,
  readonly = false,
  customActions = [],
  selectedItems = [],
  onSelectionChange
}) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragTarget, setDragTarget] = useState<{
    id: string;
    position: "before" | "after" | "inside";
  } | null>(null);

  const handleToggleSelection = useCallback((itemId: string, isMultiSelect: boolean) => {
    if (!onSelectionChange) return;

    // Always behave as multi-select - toggle item in selection
    if (selectedItems.includes(itemId)) {
      onSelectionChange(selectedItems.filter(id => id !== itemId));
    } else {
      onSelectionChange([...selectedItems, itemId]);
    }
  }, [selectedItems, onSelectionChange]);

  const handleUpdate = useCallback(
    (id: string, updates: Partial<OutlineItem>) => {
      const newData = updateItemInTree(data, id, updates);
      onChange(newData);
    },
    [data, onChange]
  );

  const handleDelete = useCallback(
    (id: string) => {
      const newData = deleteItemFromTree(data, id);
      onChange(newData);
    },
    [data, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, item: OutlineItem, parentId?: string) => {
      // Enter: Add sibling
      if (e.key === "Enter") {
        e.preventDefault();
        const newItem: OutlineItem = {
          id: generateId(),
          text: "",
          children: [],
          expanded: true,
        };
        const newData = addSiblingAfter(data, item.id, parentId, newItem);
        onChange(newData);

        // Focus new item
        setTimeout(() => {
          const newElement = document.querySelector(
            `[data-item-id="${newItem.id}"]`
          ) as HTMLElement;
          newElement?.focus();
        }, 0);
      }

      // Tab: Indent
      else if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        const newData = indentItem(data, item.id, parentId);
        onChange(newData);
      }

      // Shift+Tab: Outdent
      else if (e.key === "Tab" && e.shiftKey) {
        e.preventDefault();
        const newData = outdentItem(data, item.id, parentId);
        onChange(newData);
      }

      // Backspace on empty: Delete
      else if (e.key === "Backspace") {
        const text = (e.currentTarget as HTMLElement).textContent?.trim();
        if (!text) {
          e.preventDefault();
          handleDelete(item.id);
        }
      }
    },
    [data, onChange, handleDelete]
  );

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragTarget(null);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, targetId: string) => {
      e.preventDefault();
      e.stopPropagation();

      if (!draggedId || draggedId === targetId) {
        setDragTarget(null);
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const height = rect.height;

      let position: "before" | "after" | "inside";
      if (y < height * 0.25) {
        position = "before";
      } else if (y > height * 0.75) {
        position = "after";
      } else {
        position = "inside";
      }

      setDragTarget({ id: targetId, position });
    },
    [draggedId]
  );

  const handleDragLeave = useCallback(() => {
    setDragTarget(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetId: string, parentId?: string) => {
      e.preventDefault();

      if (!draggedId || !dragTarget || draggedId === targetId) {
        setDragTarget(null);
        return;
      }

      const draggedItem = findItem(data, draggedId);
      if (!draggedItem) return;

      // Preserve original data in case insertion fails
      const originalData = data;

      // Remove dragged item
      let newData = deleteItemFromTree(data, draggedId);

      // Add to new location
      if (dragTarget.position === "before") {
        const targetParent = findParent(newData, targetId);
        const siblings = targetParent ? targetParent.children : newData;
        const index = siblings.findIndex((s) => s.id === targetId);

        if (targetParent) {
          newData = updateItemInTree(newData, targetParent.id, {
            children: [
              ...siblings.slice(0, index),
              draggedItem,
              ...siblings.slice(index),
            ],
          });
        } else {
          newData = [...newData.slice(0, index), draggedItem, ...newData.slice(index)];
        }
      } else if (dragTarget.position === "after") {
        const targetParent = findParent(newData, targetId);
        const siblings = targetParent ? targetParent.children : newData;
        const index = siblings.findIndex((s) => s.id === targetId);

        if (targetParent) {
          newData = updateItemInTree(newData, targetParent.id, {
            children: [
              ...siblings.slice(0, index + 1),
              draggedItem,
              ...siblings.slice(index + 1),
            ],
          });
        } else {
          newData = [
            ...newData.slice(0, index + 1),
            draggedItem,
            ...newData.slice(index + 1),
          ];
        }
      } else {
        // inside
        const targetItem = findItem(newData, targetId);
        if (targetItem) {
          newData = updateItemInTree(newData, targetId, {
            children: [...targetItem.children, draggedItem],
            expanded: true,
          });
        } else {
          // Target not found after deletion — restore original to prevent data loss
          newData = originalData;
        }
      }

      onChange(newData);
      setDraggedId(null);
      setDragTarget(null);
    },
    [draggedId, dragTarget, data, onChange]
  );

  return (
    <div className={styles.outlinerContainer}>
      <div className={styles.outlinerItems}>
        {data.filter(Boolean).map((item) => (
          <OutlineItemComponent
            key={item.id}
            item={item}
            level={0}
            readonly={readonly}
            customActions={customActions}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onKeyDown={handleKeyDown}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            dragPosition={dragTarget && dragTarget.id === item.id ? dragTarget.position : null}
            isSelected={selectedItems.includes(item.id)}
            onToggleSelection={handleToggleSelection}
            selectedItems={selectedItems}
          />
        ))}
      </div>
    </div>
  );
};

export default Outliner;
